import {Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, tap} from "rxjs";
import { AllowanceEntity, IncomeTaxEntity, MenuInterface, Toeslagen, ToeslagenBerekening } from './api.bokkz.services';
import { ErrorMessage } from './rekenclient.api';

@Injectable({providedIn: 'root'})
export class BokkzTslDataService implements OnDestroy {

  public allowanceEntity = new BehaviorSubject<AllowanceEntity>(this.getFromSessionStorage<AllowanceEntity>('allowanceEntity') || {} as AllowanceEntity);
  public incomeTaxEntity = new BehaviorSubject<IncomeTaxEntity>(this.getFromSessionStorage<IncomeTaxEntity>('incomeTaxEntity') || {} as IncomeTaxEntity);
  public tslBerekening = new BehaviorSubject<ToeslagenBerekening>(this.getFromSessionStorage<ToeslagenBerekening>('tslBerekening') || {} as ToeslagenBerekening);
  public toetsingsInkomen = new BehaviorSubject<number>(0);
  public options = new BehaviorSubject<MenuInterface[]>([]);
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public hasKinderen = new BehaviorSubject<boolean>(false);
  public hasPartner = new BehaviorSubject<boolean>(false);
  public hasSstIb = new BehaviorSubject<boolean>(false);
  public hasHuurwoningen = new BehaviorSubject<boolean>(false);

  constructor() {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    this.allowanceEntity = this.allowanceEntity.pipe(
      tap((value) => this.saveToSessionStorage('allowanceEntity', value))) as BehaviorSubject<AllowanceEntity>;
    this.incomeTaxEntity = this.incomeTaxEntity.pipe(
      tap((value) => this.saveToSessionStorage('incomeTaxEntity', value))) as BehaviorSubject<IncomeTaxEntity>;
    this.tslBerekening = this.tslBerekening.pipe(
      tap((value) => this.saveToSessionStorage('tslBerekening', value))) as BehaviorSubject<ToeslagenBerekening>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
    this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
  }

  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  public updateEntity(entity: AllowanceEntity) {
    this.allowanceEntity.next(entity)
  }

  public updateToeslagen(toeslagen: Toeslagen) {

    if (toeslagen.inkomensgegevens?.at(0)?.toeslagPartner) {
      this.hasPartner.next(true);
    } else if (!toeslagen.inkomensgegevens?.at(0)?.toeslagPartner) {
      this.hasPartner.next(false);
    }

      if (toeslagen.bsn != null) {
          toeslagen.bsn = toeslagen.bsn.padStart(9, '0');
      }

      if (Array.isArray(toeslagen.inkomensgegevens)) {
      toeslagen.inkomensgegevens.forEach(inkomenGegevens => {
        if (!inkomenGegevens.toeslagPartner) {
          inkomenGegevens.geboortedatumPartner = undefined;
          inkomenGegevens.bsnPartner = undefined;
          inkomenGegevens.toetsinkomenPartner = undefined;
          inkomenGegevens.toetsvermogenPartner = undefined;
          inkomenGegevens.partnerHeeftZorgverzekering = undefined

        }
      });
    }

    if (toeslagen.kinderen !== undefined && toeslagen.kinderen.length > 0) {
      this.hasKinderen.next(true);
    } else {
      this.hasKinderen.next(false);
    }

    if (toeslagen.huurtoeslag !== undefined && toeslagen.huurtoeslag.huurwoningen !== undefined && toeslagen.huurtoeslag.huurwoningen.length > 0) {
      this.hasHuurwoningen.next(true);
    } else {
      this.hasHuurwoningen.next(false);
    }

    this.updateMenuInterface();
    return toeslagen;
  }

  public updateAllowanceTaxEntity(allowanceEntity: AllowanceEntity) {
    let updatedToeslagen = {} as Toeslagen
    if (allowanceEntity.toeslagen) {
      updatedToeslagen = this.updateToeslagen(allowanceEntity.toeslagen);
      allowanceEntity.toeslagen = updatedToeslagen
      this.allowanceEntity.next(allowanceEntity)
    }
    else {
      this.allowanceEntity.next(allowanceEntity)
    }
  }

  public updateToeslagenBerekening(toeslagenBerekening: ToeslagenBerekening) {
    this.tslBerekening.next(toeslagenBerekening)
  }

  public updateErrorResponse(errorResponse: ErrorMessage[]) {
    this.errorResponse.next(errorResponse);
  }

  public updateAantalValidaties(aantalValidaties: number) {
    this.aantalValidaties.next(aantalValidaties)
  }

  public updateMenuInterface() {
    const menuInterface : MenuInterface[] = [
      {
        value: 0,
        id: 'persoonlijk',
        label: '1. Gegevens & zorgtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Aanvrager',
            pageMapping: 'Aanvrager',
            visible: true,
            value: 11
          },
          {
            label: 'Partner',
            pageMapping: 'Partner',
            visible: this.hasPartner.value,
            value: 11
          }

        ],
      },
      {
        value: 1,
        id: 'kinderen',
        label: '2. Kindgebonden budget',
        visible: true,
        subonderdelen: [
          {
            label: 'Kinderen',
            pageMapping: 'Kinderen',
            visible: true,
            value: 21
          }
        ],
      },
      {
        value: 2,
        id: 'kinderopvang',
        label: '3. Kinderopvangtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Kinderopvang',
            pageMapping: 'Kinderopvang',
            visible: true,
            value: 31
          }
        ],
      },
      {
        value: 3,
        id: 'huurwoning',
        label: '4. Huurtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Huurwoning',
            pageMapping: 'Huurwoning',
            visible: true,
            value: 41
          }
        ],
      },
      {
        value: 4,
        id: 'overzicht',
        label: 'Overzicht',
        visible: true,
        subonderdelen: [
          {
            label: 'Voorschot',
            pageMapping: 'Voorschot',
            visible: true,
            value: 51
          },
          {
            label: 'Berekening',
            pageMapping: 'Berekening',
            visible: true,
            value: 52
          }
        ],
      },
    ];
    this.options.next(menuInterface);
  }

  public updateToeslagenFromIbAangifte(toeslagFromService: Toeslagen) {
    const bestaandObject = this.getFromSessionStorage<AllowanceEntity>('allowanceEntity') || {} as AllowanceEntity;
    if (bestaandObject.toeslagen) {
      bestaandObject.toeslagen.berekeningsjaar = toeslagFromService.berekeningsjaar;
      bestaandObject.toeslagen.bsn = toeslagFromService.bsn;
      bestaandObject.toeslagen.aanvrager=toeslagFromService.aanvrager;
      bestaandObject.toeslagen.geboortedatumAanvrager = toeslagFromService.geboortedatumAanvrager;
      bestaandObject.toeslagen.inkomensgegevens = toeslagFromService.inkomensgegevens;
      if (bestaandObject.toeslagen.kinderen === undefined) {
        bestaandObject.toeslagen.kinderen = toeslagFromService.kinderen;
      }
    }
    this.hasSstIb.next(true);
    this.allowanceEntity.next(bestaandObject);
  }

  ngOnDestroy(): void {
    if (this.hasPartner){
      this.hasPartner?.unsubscribe()
    }
    if (this.hasKinderen){
      this.hasKinderen?.unsubscribe()
    }
    if (this.hasHuurwoningen){
      this.hasHuurwoningen?.unsubscribe()
    }
    if (this.hasSstIb){
      this.hasSstIb?.unsubscribe()
    }
    if (this.errorResponse){
      this.errorResponse?.unsubscribe()
    }
    if (this.aantalValidaties){
      this.aantalValidaties?.unsubscribe()
    }
    if (this.allowanceEntity){
      this.allowanceEntity.unsubscribe()
    }
    if (this.tslBerekening){
      this.tslBerekening?.unsubscribe()
    }
    if (this.toetsingsInkomen){
      this.toetsingsInkomen?.unsubscribe()
    }
    if (this.options){
      this.options?.unsubscribe()
    }
  }
}
